import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBookOpen,
  faGraduationCap,
  faShieldHalved,
  faCircleInfo,
} from '@fortawesome/free-solid-svg-icons';
import BackButton from '../../../components/BackButton/BackButton';
import WHArticleList from '../../../components/WHArticleList/WHArticleList';
import './Education.css';

type TabKey = 'education' | 'privacy';

const Education: React.FC = () => {
  const { t } = useTranslation(['womensHealth', 'common']);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabKey>(
    (searchParams.get('tab') as TabKey) || 'education'
  );

  const tabs: { key: TabKey; label: string; icon: typeof faGraduationCap }[] = [
    { key: 'education', label: 'Cycle Education', icon: faGraduationCap },
    { key: 'privacy', label: 'Privacy & Safety', icon: faShieldHalved },
  ];

  return (
    <div className="wh-edu-page">
      <section className="wh-edu-hero">
        <div className="wh-edu-hero-circle wh-edu-hero-circle-1" />
        <div className="wh-edu-hero-circle wh-edu-hero-circle-2" />
        <Container>
          <BackButton />
          <div className="wh-edu-hero-content">
            <div className="wh-edu-hero-badge">
              <FontAwesomeIcon icon={faBookOpen} />
              Puberty &amp; Menstrual
            </div>
            <h1 className="wh-edu-hero-title">{t('womensHealth:hub.education')}</h1>
            <p className="wh-edu-hero-subtitle">
              Learn about your body, understand your menstrual cycle, and discover important health information in a safe, supportive space.
            </p>
          </div>
        </Container>
      </section>

      <Container>
        <div className="wh-edu-tabs-container">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`wh-edu-tab-btn ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <FontAwesomeIcon icon={tab.icon} />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="wh-edu-section-header">
          <div className="wh-edu-section-header-text">
            <h2>
              <FontAwesomeIcon icon={activeTab === 'education' ? faGraduationCap : faShieldHalved} />
              {activeTab === 'education' ? 'Cycle Education' : 'Privacy & Safety'}
            </h2>
            <p>
              {activeTab === 'education'
                ? 'Expand each topic below to learn about the different aspects of menstrual health.'
                : 'Your privacy matters. Learn how we protect your data and tips for staying safe.'}
            </p>
          </div>
        </div>

        <div className="wh-edu-tab-content">
          {activeTab === 'education' ? (
            <WHArticleList
              stage="puberty"
              contentType="cycle_education"
              variant="accordion"
              icon={faGraduationCap}
              accentColor="#ec4899"
            />
          ) : (
            <WHArticleList
              stage="puberty"
              contentType="privacy_safety"
              variant="card"
              icon={faShieldHalved}
              accentColor="#10b981"
            />
          )}
        </div>

        <div className="wh-edu-tip-card">
          <div className="wh-edu-tip-icon">
            <FontAwesomeIcon icon={faCircleInfo} />
          </div>
          <div className="wh-edu-tip-text">
            <strong>Did you know?</strong> Understanding your menstrual cycle can help you predict mood changes, plan activities around your energy levels, and identify potential health concerns early. Knowledge is your best tool for taking charge of your health.
          </div>
        </div>
      </Container>
    </div>
  );
};

export default Education;
